/**
 * First-touch referral attribution for display ads and partner codes.
 * Cookie: qixit_ref_{env} on .qixit.com, 30-day TTL, SameSite=Lax.
 */

export interface StoredReferral {
  ref: string;
  src?: string;
  cmp?: string;
  landing?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  ts: number;
}

const COOKIE_BASE = 'qixit_ref';
const MAX_AGE_SECONDS = 30 * 24 * 60 * 60; // 30 days
export const DEFAULT_AD_LANDING = 'https://2waymicropay.com/wp/';

type AuthEnvironment = 'dev' | 'stage' | 'prod';

function getAuthEnvironment(): AuthEnvironment {
  if (typeof window === 'undefined') return 'prod';
  const hostname = window.location.hostname.toLowerCase();
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return 'dev';
  }
  if (hostname.startsWith('st') || hostname.includes('-stage') || hostname.includes('.stage.')) {
    return 'stage';
  }
  return 'prod';
}

export function getReferralCookieName(env?: AuthEnvironment): string {
  return `${COOKIE_BASE}_${env ?? getAuthEnvironment()}`;
}

function getCookieDomain(): string {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '';
  if (hostname.includes('qixit.com')) return '.qixit.com';
  return '';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  const domain = getCookieDomain();
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  let cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
  if (domain) cookie += `; Domain=${domain}`;
  if (isSecure) cookie += '; Secure';
  document.cookie = cookie;
}

function clearCookie(name: string): void {
  if (typeof document === 'undefined') return;
  const domain = getCookieDomain();
  let cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  if (domain) cookie += `; Domain=${domain}`;
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') cookie += '; Secure';
  document.cookie = cookie;
}

export function getStoredReferral(): StoredReferral | null {
  const raw = readCookie(getReferralCookieName());
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredReferral;
    if (!parsed?.ref || typeof parsed.ref !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** First-touch: write only when no cookie exists yet. */
export function captureReferralFromUrl(search?: string): boolean {
  if (typeof window === 'undefined') return false;
  if (getStoredReferral()) return false;

  const params = new URLSearchParams(search ?? window.location.search);
  // Hash-router apps may put query after #
  if (!params.get('ref') && !params.get('RefID') && !params.get('refid') && window.location.hash.includes('?')) {
    const hashQuery = window.location.hash.split('?')[1] ?? '';
    const hashParams = new URLSearchParams(hashQuery);
    for (const key of ['ref', 'RefID', 'refid', 'source', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content']) {
      const v = hashParams.get(key);
      if (v && !params.get(key)) params.set(key, v);
    }
  }

  const ref =
    params.get('ref') ||
    params.get('RefID') ||
    params.get('refid') ||
    '';
  if (!ref.trim()) return false;

  const payload: StoredReferral = {
    ref: ref.trim(),
    src: params.get('utm_source') || params.get('source') || undefined,
    cmp: params.get('utm_campaign') || undefined,
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    landing: window.location.href,
    ts: Date.now(),
  };

  writeCookie(getReferralCookieName(), JSON.stringify(payload), MAX_AGE_SECONDS);
  return true;
}

export function clearStoredReferral(): void {
  clearCookie(getReferralCookieName());
}

export function appendReferralToRegisterUrl(baseUrl: string): string {
  const stored = getStoredReferral();
  if (!stored?.ref) return baseUrl;
  try {
    const hasHash = baseUrl.includes('#');
    if (hasHash) {
      const [before, after = ''] = baseUrl.split('#');
      const pathAndQuery = after.startsWith('/') ? after : `/${after}`;
      const qIndex = pathAndQuery.indexOf('?');
      const path = qIndex >= 0 ? pathAndQuery.slice(0, qIndex) : pathAndQuery;
      const existing = qIndex >= 0 ? pathAndQuery.slice(qIndex + 1) : '';
      const params = new URLSearchParams(existing);
      if (!params.get('ref') && !params.get('RefID') && !params.get('refid')) {
        params.set('ref', stored.ref);
      }
      const qs = params.toString();
      return `${before}#${path}${qs ? `?${qs}` : ''}`;
    }
    const url = new URL(baseUrl, typeof window !== 'undefined' ? window.location.origin : 'https://mail.qixit.com');
    if (!url.searchParams.get('ref') && !url.searchParams.get('RefID') && !url.searchParams.get('refid')) {
      url.searchParams.set('ref', stored.ref);
    }
    return url.toString();
  } catch {
    const sep = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${sep}ref=${encodeURIComponent(stored.ref)}`;
  }
}

export function getTrackClickBaseUrl(): string {
  if (typeof window === 'undefined') return 'https://mail.qixit.com';
  const hostname = window.location.hostname.toLowerCase();
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Same-origin through mailbox-web Vite proxy (:3000 → MailBox.API :5220)
    return window.location.origin;
  }
  if (hostname.startsWith('st') || hostname.includes('-stage') || hostname.includes('.stage.')) {
    return 'https://stmail.qixit.com';
  }
  return 'https://mail.qixit.com';
}

export function buildAdTrackingUrl(
  referralCode: string,
  options?: {
    to?: string;
    trackBaseUrl?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
  }
): string {
  const base = (options?.trackBaseUrl ?? getTrackClickBaseUrl()).replace(/\/$/, '');
  const params = new URLSearchParams();
  params.set('ref', referralCode);
  params.set('to', options?.to ?? DEFAULT_AD_LANDING);
  if (options?.utm_source) params.set('utm_source', options.utm_source);
  if (options?.utm_medium) params.set('utm_medium', options.utm_medium);
  if (options?.utm_campaign) params.set('utm_campaign', options.utm_campaign);
  if (options?.utm_content) params.set('utm_content', options.utm_content);
  return `${base}/api/track/click?${params.toString()}`;
}
